import React, { useState } from 'react';
import { Box, Typography, Card, Button, Avatar, useTheme, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import { SbChip } from '../../pages/GetRatesV2/CSPList/styles';

interface ShipmentDetail {
  shipmentIndex: number;
  chargeableWeight: number;
  totalPricing: number;
  courierCharge: number;
  tax: number;
  masked: boolean;
}

interface AggregatedRate {
  carrierName: string;
  systemName: string;
  warehouseId: string;
  systemID: string;
  serviceImage: string;
  code: string;
  currency: string;
  warehouseName: string;
  transitTime: number;
  totalChargeableWeight: number;
  totalCost: number;
  processed: number;
  unprocessed: number;
  shipmentDetails: ShipmentDetail[];
}

const findCheapestAndExpensive = (rates: AggregatedRate[]) => {
  if (!rates?.length) return { cheapest: null, expensive: null };
  let cheapest = rates[0];
  let expensive = rates[0];
  for (let i = 1; i < rates.length; i++) {
    if (rates[i].totalCost < cheapest.totalCost) cheapest = rates[i];
    if (rates[i].totalCost > expensive.totalCost) expensive = rates[i];
  }
  return { cheapest, expensive };
};

export const WarehouseBookingSection: React.FC<{
  onClose: () => void;
  onBook: (carrierName: string, systemName: string, warehouseID: string, systemID: string) => void;
}> = ({ onClose, onBook }) => {
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState<AggregatedRate | null>(null);
  const { data, loading } = useSelector((state: RootStateType) => state.warehouseRates);
  const { cheapest, expensive } = findCheapestAndExpensive(data?.aggregatedRates || []);
  const handleSelect = (option: AggregatedRate) => setSelectedOption(option);

  const handleBook = () => {
    if (selectedOption) {
      onBook(
        selectedOption.carrierName,
        selectedOption.systemName,
        selectedOption.warehouseId,
        selectedOption.systemID,
      );
    }
  };

  const formatCurrency = (amount: number, currency?: string) => {
    const validCurrency = currency && currency.trim() ? currency : 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: validCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <Box>
      {/* Header Section */}
      <Grid
        container
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          borderRadius: 1,
          px: 2,
          py: 1,
          mb: 1,
        }}
      >
        <Grid item xs={3}>
          <Typography sx={{ textAlign: 'center', fontSize: '14px' }} variant="subtitle2" fontWeight="bold">
            Carrier
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ textAlign: 'center' }}>
            {' '}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ textAlign: 'center', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            Chargeable Wt
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ textAlign: 'center', fontSize: '14px' }}>
            Cost
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle2" fontWeight="bold"></Typography>
        </Grid>
      </Grid>

      {/* ✅ Cards Section (aligned perfectly under header) */}
      {!data?.aggregatedRates || data.aggregatedRates.length === 0 || loading ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {loading ? 'Loading rates...' : 'No rates available'}
        </Box>
      ) : (
        data.aggregatedRates.map((rate: AggregatedRate, index: number) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              mb: 1,
              borderRadius: 2,
              p: 2,
              transition: 'all 0.2s ease',
              // border: selectedOption === rate ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
            }}
          >
            <Grid
              container
              alignItems="center"
              spacing={0}
              sx={{ cursor: 'pointer' }}
              onClick={() => handleSelect(rate)}
            >
              {/* Column 1: Carrier */}
              <Grid item xs={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Avatar
                    src={rate.serviceImage}
                    sx={{
                      width: 100,
                      height: 60,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      p: 1,
                    }}
                    variant="rounded"
                  />
                  <Box>
                    <Typography className="md-text font-semibold" noWrap>
                      {rate.carrierName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {rate.transitTime} Days
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight="600" noWrap>
                      Warehouse - {rate.warehouseName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight="600" noWrap>
                      Processed - {rate.processed} | Unprocessed - {rate.unprocessed}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Column 2: Tag (Cheapest / Expensive) */}
              <Grid item xs={4} sx={{ textAlign: 'center' }}>
                {rate === cheapest && (
                  <SbChip
                    className="md-text font-semibold"
                    sx={{
                      color: '#12647E',
                      background: '#ADE6FF',
                      '& .MuiChip-icon': { color: '#12647E' },
                    }}
                    icon={<AccountBalanceWalletIcon />}
                    label="Cheapest"
                  />
                )}
                {rate === expensive && (
                  <SbChip
                    className="md-text font-semibold"
                    sx={{
                      color: '#127E44',
                      background: '#ADFFB5',
                      '& .MuiChip-icon': { color: '#127E44' },
                    }}
                    icon={<BoltIcon />}
                    label="Fastest"
                  />
                )}
                {rate !== cheapest && rate !== expensive && <Box sx={{ height: 28, minWidth: 120 }} />}
              </Grid>

              {/* Column 3: Chargeable Weight */}
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight="600" sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {rate.totalChargeableWeight?.toFixed(2)} kg
                </Typography>
              </Grid>

              {/* Column 4: Cost */}
              <Grid item xs={1}>
                <Typography variant="body1" fontWeight="600" sx={{ textAlign: 'center' }}>
                  {formatCurrency(rate.totalCost, rate.currency)}
                </Typography>
              </Grid>

              {/* Column 5: Action */}
              <Grid item xs={2} sx={{ textAlign: 'center' }}>
                <Button
                  variant={selectedOption === rate ? 'contained' : 'outlined'}
                  onClick={e => {
                    e.stopPropagation();
                    handleSelect(rate);
                  }}
                  sx={{
                    borderRadius: 5,
                    px: 2.5,
                    py: 1,
                    fontSize: '14px',
                    minWidth: 100,
                    fontWeight: '600',
                  }}
                >
                  {selectedOption === rate ? 'Selected' : 'Select'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        ))
      )}

      {/* Footer Section */}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1, fontSize: '14px', fontWeight: '600' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBook}
            disabled={!selectedOption}
            sx={{
              borderRadius: 5,
              px: 3,
              py: 1,
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: selectedOption ? theme.palette.primary.main : 'grey.500',
              '&:hover': {
                backgroundColor: selectedOption ? theme.palette.primary.dark : 'grey.700',
              },
            }}
          >
            Book Now
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
